import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import {
  EOBExtractionResponseDTO,
  EOBExtractionProgressNotificationDTO,
  FileUploadSasUrlResponseDTO,
  InitiateEOBDocumentExtractionResponseDTO,
  BlobMetaData,
  EOBExtractionJobResultDTO,
} from 'dtos';
import {
  EOBExtractionJobStatus,
} from 'dtos/eOBExtractionJobStatus';
import {
  BlockBlobClient,
} from '@azure/storage-blob';
import {
  useNotify,
} from 'core/hooks';
import useSignalREffect from 'views/EMR/hooks/signalR';
import uniqueId from 'lodash/uniqueId';
import {
  getErrorMessage,
} from 'utils/errors';
import useFailSafeRetry from 'views/EMR/components/Snap/useFailSafeRetry';
import {
  ParseEOBSource,
} from 'pages/Dashboard/pages/Billing/Payments/ParseEOBDialog/utils';
import {
  formatFileSize,
} from 'utils/string';
import useStepTimingLogger from 'core/hooks/useStepTimeLogger';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  EOBUploadSasUrlRequestDTO,
} from 'dtos/eOBUploadSasUrlRequestDTO';
import {
  getApiBillingGetEOBExtractionResult,
  postApiBillingGetEOBUploadUrl,
  postApiBillingInitiateEOBDocumentExtraction,
} from 'endpoints';
import {
  isNilOrZero,
} from '@populateui/utils';
import compact from 'lodash/compact';

type EOBExtractionState = {
  eobExtractionJobId: number | null;
  enableUniqueId: string | null;
  status: EOBExtractionJobStatus | null;
};

type UseStartEOBExtractionProps = {
  onExtracted: (extracted: EOBExtractionResponseDTO, eobExtractionJobId?: number) => void;
  onExtractionFailed: (errorMessage: string) => void;
};

const defaultEOBExtractionState: EOBExtractionState = {
  eobExtractionJobId: null,
  enableUniqueId: null,
  status: null,
};

const EOB_LOG_PREFIX = {
  EXTRACTION: 'EOBExtraction',
  EOB_UPLOAD_URL: 'GotEOBUploadUrl',
  BLOB_UPLOAD: 'BlobUpload',
  INITIATE_EXTRACTION: 'InitiatedEOBExtraction',
  ON_EXTRACTION_PROGRESS: 'OnEOBExtractionProgress',
  EXTRACTION_COMPLETED: 'EOBExtractionCompleted',
  EXTRACTION_FAILED: 'EOBExtractionFailed',
  EXTRACTION_FETCH_FAILED: 'EOBExtractionFetchFailed',
} as const;

const EOB_MIME_TYPE_BY_EXTENSION: Record<string, string> = Object.freeze({
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  tif: 'image/tiff',
  tiff: 'image/tiff',
});

function resolveEOBMimeType(file: File): string {
  if (file.type) {
    return file.type;
  }

  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EOB_MIME_TYPE_BY_EXTENSION[extension] ?? 'application/octet-stream';
}

export function useGetEOBUploadUrl(): UseMutationResult<
  FileUploadSasUrlResponseDTO,
  Error,
  EOBUploadSasUrlRequestDTO> {
  return useMutation<FileUploadSasUrlResponseDTO, Error, EOBUploadSasUrlRequestDTO>({
    mutationFn: (data) => postApiBillingGetEOBUploadUrl(data),
  });
}

export function useInitiateEOBDocumentExtraction(): UseMutationResult<
  InitiateEOBDocumentExtractionResponseDTO,
  Error,
  BlobMetaData> {
  return useMutation<InitiateEOBDocumentExtractionResponseDTO, Error, BlobMetaData>({
    mutationFn: (data) => postApiBillingInitiateEOBDocumentExtraction(data),
  });
}

export function useGetEOBExtractionResult(
  eobExtractionJobId: number = 0,
  uniqueId: string | null = null,
): UseQueryResult<EOBExtractionJobResultDTO, Error> {
  return useQuery<EOBExtractionJobResultDTO, Error>({
    queryKey: compact(['eob-extraction-result', eobExtractionJobId, uniqueId]),
    queryFn: () => getApiBillingGetEOBExtractionResult({ eobExtractionJobId }),
    enabled: !isNilOrZero(eobExtractionJobId) && !isNil(uniqueId),
  });
}

export function useStartEOBExtraction({
  onExtracted,
  onExtractionFailed,
}: UseStartEOBExtractionProps) {
  const notify = useNotify();
  const activeFileSizeRef = useRef<string>('');
  const isCancelledRef = useRef(false);
  const { mutateAsync: getEOBUploadUrl } = useGetEOBUploadUrl();
  const {
    mutateAsync: initiateEOBDocumentExtraction,
    isPending: isInitiating,
  } = useInitiateEOBDocumentExtraction();
  const {
    start: startLog,
    log,
    end: endLog,
  } = useStepTimingLogger(EOB_LOG_PREFIX.EXTRACTION);
  const [
    extractionState,
    setExtractionState,
  ] = useState<EOBExtractionState>(defaultEOBExtractionState);

  const {
    eobExtractionJobId,
    enableUniqueId,
    status,
  } = extractionState;

  const getLogExtra = useCallback((extra?: Record<string, unknown>) => ({
    fileSize: activeFileSizeRef.current,
    ...extra,
  }), []);

  const clearExtractionState = useCallback(() => {
    activeFileSizeRef.current = '';
    setExtractionState(defaultEOBExtractionState);
  }, []);

  const {
    data: extractionResult,
    error: extractionResultError,
    refetch,
    isFetching,
  } = useGetEOBExtractionResult(
    eobExtractionJobId ?? 0,
    enableUniqueId,
  );

  const failSafeRetry = useFailSafeRetry({
    label: 'EOBExtraction',
    jobId: eobExtractionJobId,
    isTerminal: status === EOBExtractionJobStatus.Completed
      || status === EOBExtractionJobStatus.Failed,
    isCancelled: false,
    config: {
      retryIntervals: [
        3 * 60 * 1000,
        7 * 60 * 1000,
        12 * 60 * 1000,
        18 * 60 * 1000,
        25 * 60 * 1000,
      ],
    },
    onRetry: () => {
      refetch();
    },
  });

  const cancelExtraction = useCallback(() => {
    isCancelledRef.current = true;
    clearExtractionState();
    failSafeRetry.stop();
  }, [clearExtractionState, failSafeRetry]);

  const startEOBExtraction = useCallback(async (file: File, source: ParseEOBSource) => {
    isCancelledRef.current = false;
    const fileSize = formatFileSize(file.size);
    activeFileSizeRef.current = fileSize;
    startLog(`${source}-${fileSize}`, getLogExtra());

    try {
      const mimeType = resolveEOBMimeType(file);
      const uploadMetadata = await getEOBUploadUrl({
        fileName: file.name,
        mimeType,
      });
      log(EOB_LOG_PREFIX.EOB_UPLOAD_URL, getLogExtra({
        blobPath: uploadMetadata.blobPath ?? null,
      }));

      const blobClient = new BlockBlobClient(uploadMetadata.uploadUrl ?? '');
      await blobClient.uploadData(file, {
        blobHTTPHeaders: { blobContentType: mimeType },
        blockSize: 8 * 1024 * 1024,
        concurrency: 4,
      });
      log(EOB_LOG_PREFIX.BLOB_UPLOAD, getLogExtra({
        blobPath: uploadMetadata.blobPath ?? null,
      }));

      const response = await initiateEOBDocumentExtraction({
        fileName: file.name,
        blobPath: uploadMetadata.blobPath ?? '',
        byteCount: file.size,
        mimeType,
        uploadedAt: new Date().toISOString(),
      });
      log(EOB_LOG_PREFIX.INITIATE_EXTRACTION, getLogExtra({
        eobExtractionJobId: response.eobExtractionJobId ?? null,
      }));

      if (isCancelledRef.current) {
        return;
      }

      setExtractionState({
        eobExtractionJobId: response.eobExtractionJobId ?? null,
        enableUniqueId: null,
        status: EOBExtractionJobStatus.Initiated,
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error) ?? 'Failed to extract EOB document';
      onExtractionFailed(errorMessage as string);
      endLog(getLogExtra({ error: errorMessage }), true);
      throw error;
    }
  }, [
    endLog,
    getEOBUploadUrl,
    getLogExtra,
    initiateEOBDocumentExtraction,
    log,
    onExtractionFailed,
    startLog,
  ]);

  useSignalREffect('OnEOBExtractionProgress', (data: EOBExtractionProgressNotificationDTO) => {
    if (data.eobExtractionJobId !== eobExtractionJobId) {
      return;
    }

    const nextStatus = data.status ?? null;

    log(EOB_LOG_PREFIX.ON_EXTRACTION_PROGRESS, getLogExtra({ nextStatus }));

    setExtractionState((prev) => ({
      ...prev,
      status: nextStatus as EOBExtractionJobStatus,
      enableUniqueId: nextStatus === EOBExtractionJobStatus.Completed
        || nextStatus === EOBExtractionJobStatus.Failed
        ? uniqueId('eob-extraction-')
        : prev.enableUniqueId,
    }));
  }, [eobExtractionJobId, getLogExtra, log]);

  useEffect(() => {
    if (!isNil(eobExtractionJobId)) {
      failSafeRetry.start();
    }
  }, [eobExtractionJobId]);

  useEffect(() => {
    if (!isNil(extractionResult)) {
      if (extractionResult.status === EOBExtractionJobStatus.Completed
        && !isNil(extractionResult.result)
      ) {
        const {
          claimsPresentInEobButMissingInSystem,
          eobWithInsurancePayment,
        } = extractionResult.result ?? {};
        const missingClaims = claimsPresentInEobButMissingInSystem?.length
        ?? 0;
        const presentClaims = eobWithInsurancePayment?.eob?.eobClaimPayments?.length
        ?? 0;

        log(EOB_LOG_PREFIX.EXTRACTION_COMPLETED, getLogExtra({
          status: extractionResult.status,
          eobExtractionJobId,
          missingClaims,
          presentClaims,
          totalClaims: missingClaims + presentClaims,
        }));
        endLog(getLogExtra({
          status: extractionResult.status,
          eobExtractionJobId,
        }));
        onExtracted(extractionResult.result, eobExtractionJobId ?? undefined);
        clearExtractionState();
        return;
      }
      if (extractionResult.status === EOBExtractionJobStatus.Failed) {
        const errorMessage = extractionResult.errorMessage ?? 'Failed to extract EOB document';
        log(EOB_LOG_PREFIX.EXTRACTION_FAILED, getLogExtra({
          status: extractionResult.status,
          errorMessage,
          eobExtractionJobId,
        }), true);
        endLog(getLogExtra({
          status: extractionResult.status,
          errorMessage,
          eobExtractionJobId,
        }), true);
        notify({ message: errorMessage, variant: 'error' });
        onExtractionFailed(errorMessage);
        clearExtractionState();
      }
    }
  }, [
    eobExtractionJobId,
    endLog,
    extractionResult,
    getLogExtra,
    log,
    onExtracted,
    onExtractionFailed,
    notify,
    clearExtractionState,
  ]);

  useEffect(() => {
    if (isNil(extractionResultError)) {
      return;
    }

    const error = getErrorMessage(extractionResultError) ?? 'Failed to extract EOB document';
    const errorMessage = typeof error === 'string' ? error : 'Failed to extract EOB document';
    log(EOB_LOG_PREFIX.EXTRACTION_FETCH_FAILED, getLogExtra({
      errorMessage,
      eobExtractionJobId,
    }), true);
    endLog(getLogExtra({
      errorMessage,
      eobExtractionJobId,
    }), true);
    notify({ message: error, variant: 'error' });
    onExtractionFailed(errorMessage);
    clearExtractionState();
  }, [
    eobExtractionJobId,
    extractionResultError,
    endLog,
    getLogExtra,
    log,
    onExtractionFailed,
    notify,
    clearExtractionState,
  ]);

  return {
    startEOBExtraction,
    cancelExtraction,
    extractionState,
    isProcessing: isInitiating
      || status === EOBExtractionJobStatus.Initiated
      || isFetching,
  };
}
